
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface SearchOptionsPanelProps {
  useRoleTitle: boolean;
  setUseRoleTitle: (value: boolean) => void;
  useRoleLocation: boolean;
  setUseRoleLocation: (value: boolean) => void;
  useOpenToWork: boolean;
  setUseOpenToWork: (value: boolean) => void;
  aiEngine: string;
  setAiEngine: (value: string) => void;
  processLog: string;
  handleGenerateAll: () => void;
  isGeneratingAll: boolean;
  searching: boolean;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  handleClearCandidates: () => void;
  isClearing: boolean;
}

export const SearchOptionsPanel = ({
  useRoleTitle,
  setUseRoleTitle,
  useRoleLocation,
  setUseRoleLocation,
  useOpenToWork,
  setUseOpenToWork,
  aiEngine,
  setAiEngine,
  processLog,
  handleGenerateAll,
  isGeneratingAll,
  searching,
  textAreaRef,
  handleClearCandidates,
  isClearing
}: SearchOptionsPanelProps) => {
  return (
    <div className="w-full">
      <div className="flex flex-col gap-2 w-full">
        <div className="flex justify-between items-center w-full my-[4px]">
          <div className="flex items-center space-x-4">
            <Label className="text-[11px] text-white">
              Search using
            </Label>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="role-title" 
                  checked={useRoleTitle} 
                  onCheckedChange={checked => setUseRoleTitle(checked as boolean)} 
                />
                <Label htmlFor="role-title" className="text-[11px] text-primary">Role title</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="role-location" 
                  checked={useRoleLocation} 
                  onCheckedChange={checked => setUseRoleLocation(checked as boolean)} 
                />
                <Label htmlFor="role-location" className="text-[11px] text-primary">Role location</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="open-to-work" 
                  checked={useOpenToWork} 
                  onCheckedChange={checked => setUseOpenToWork(checked as boolean)} 
                />
                <Label htmlFor="open-to-work" className="text-[11px] text-primary">Open to work</Label>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Label className="text-[11px] text-white">Choose AI Engine:</Label>
            <RadioGroup 
              className="flex flex-row gap-2" 
              value={aiEngine} 
              onValueChange={setAiEngine} 
              defaultValue="openai"
            >
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="openai" id="ai-openai" />
                <Label htmlFor="ai-openai" className="text-[11px] text-white">OpenAI</Label>
              </div>
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="deepseek" id="ai-deepseek" />
                <Label htmlFor="ai-deepseek" className="text-[11px] text-white">DeepSeek</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <div className="flex items-start gap-4 w-full py-0 my-0">
          <div className="flex flex-col gap-2">
            <Button 
              onClick={handleGenerateAll} 
              disabled={isGeneratingAll || searching} 
              className="w-[160px] text-black bg-green-400 hover:bg-green-300 text-[13px] h-[35px] py-0"
            >
              AI Candidate Search
            </Button>
            <Button
              onClick={handleClearCandidates}
              disabled={isClearing || searching || isGeneratingAll}
              className="w-[160px] text-white bg-red-500 hover:bg-red-400 text-[13px] h-[35px] py-0"
            >
              Clear Candidates
            </Button>
          </div>
          <Textarea 
            rows={6} 
            ref={textAreaRef} 
            value={processLog} 
            readOnly 
            className="flex-1 h-[54px] text-[11px] bg-secondary-light/5 text-primary-light whitespace-pre-wrap my-0 py-[3px] border-primary" 
          />
        </div>
      </div>
    </div>
  );
};
